import { Option } from "components/parts/Input/SelectBox/SelectBox";
import { PageLevel, useConsoleState } from "providers/ConsoleStateProvider";
import { useLoadingState } from "providers/LoadingProvider";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { CreateMarkedSubjectInput, Subject } from "types/graphqlapi";
import { Status } from "types/status";
import {
  createMarkedSubjectMutation,
  deleteMarkedSubjectMutation,
  fetchMarkedSubjectByUserIdQuery,
} from "utils/apis/markedSubject";
import { getGroupQuery, listGroupsQuery } from "utils/apis/group";
import { listInterviewsQuery } from "utils/apis/interview";
import { listAllSubjectsQuery, listSubjectsQuery } from "utils/apis/subject";
import {
  listUsersByGroupQuery,
  listAdminGroupUserQuery,
  listUsersQuery,
  listGroupAffiliationUsers,
} from "utils/apis/user";
import { getDisplayMD } from "utils/common";
import { v4 as uuidv4 } from "uuid";

export const decideSubjectStatus = async (
  subject: Subject
): Promise<Status> => {
  const interviewList = await listInterviewsQuery(subject.id);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // 実査が無い場合、未実施
  if (!interviewList || interviewList.length === 0) return "NOT_STARTED";

  // 全ての実査の開始日が今日よりも遅い場合、未実施
  const isNotStarted = interviewList.every((interview) => {
    const startDate = new Date(interview.scheduled_start_date as string);
    return startDate > today;
  });
  if (isNotStarted) return "NOT_STARTED";

  // 全ての実査の終了日を過ぎた場合、実施済
  const isDone = interviewList.every((interview) => {
    const endDate = new Date(interview.scheduled_end_date as string);
    return today > endDate;
  });
  if (isDone) return "DONE";

  return "DOING";
};

const returnAvailableSubjectList = async (
  resultSubjectList: Subject[] | undefined
) => {
  const groupList = await listGroupsQuery();
  return resultSubjectList?.filter((result) =>
    groupList?.map((group) => group.id).includes(result.group_id)
  );
};

const returnMainUserBySubject = async (subject: Subject) => {
  const resultList = await listUsersQuery();
  const main_user_name_asmarq = resultList?.find(
    (result) => result.id === subject.main_user_id_asmarq
  )?.name as string;
  const main_user_name_group = resultList?.find(
    (result) => result.id === subject.main_user_id_group
  )?.name as string;
  return { main_user_name_asmarq, main_user_name_group };
};

export const returnSubjectDate = async (subjectId: string) => {
  const subjectDate = await listInterviewsQuery(subjectId).then((result) => {
    const sortedStartDateList = result
      ?.map((result) => new Date(result?.scheduled_start_date as string))
      .sort((a, b) => (a > b ? 1 : -1));
    const sortedEndDateList = result
      ?.map((result) => new Date(result?.scheduled_end_date as string))
      .sort((a, b) => (a < b ? 1 : -1));

    return {
      first_interview_date:
        sortedStartDateList && sortedStartDateList[0]
          ? getDisplayMD(sortedStartDateList[0])
          : "未設定",
      last_interview_date:
        sortedEndDateList && sortedEndDateList[0]
          ? getDisplayMD(sortedEndDateList[0])
          : "未設定",
    };
  });
  return subjectDate;
};

export const useUserList = () => {
  const history = useHistory();
  const { selectedGroupId, isOneGroupUser, userId } = useConsoleState();
  const [asmarqManager, setAsmarqManagerList] = useState<Option[]>([]);
  const [optionGroupUserList, setOptionGroupUserList] = useState<Option[]>([]);

  /**
   * プルダウン用グループユーザーリスト取得
   * @returns
   */
  const fetchOptionGroupUserList = async () => {
    try {
      const optionGroupUserList = isOneGroupUser
        ? await listUsersByGroupQuery(selectedGroupId)
        : await listGroupAffiliationUsers();
      setOptionGroupUserList(optionGroupUserList);
    } catch (error) {
      console.error(error);
      alert("エラーが発生しました。");
      history.replace("/auth/login");
    }
  };

  /**
   * プルダウン用アスマークユーザーリスト取得
   * @returns
   */
  const fetchAsmarqManagerList = async () => {
    try {
      const asmarqManagerList = await listAdminGroupUserQuery(userId);

      setAsmarqManagerList(asmarqManagerList);
    } catch (error) {
      console.error(error);
      alert("エラーが発生しました。");
      history.replace("/auth/login");
    }
  };

  useEffect(() => {
    fetchAsmarqManagerList();
    fetchOptionGroupUserList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { asmarqUserList: asmarqManager, optionGroupUserList };
};

export const useOptionGroupList = () => {
  const history = useHistory();
  const [optionGroupList, setOptionGroupList] = useState<Option[]>([]);

  const fetchOptionGroupList = async () => {
    try {
      const OptionGroupResultList = await listGroupsQuery().then((result) =>
        result
          ? result
              .filter((result) => result.id != "0")
              .map((result) => {
                return {
                  value: result.id,
                  text: result.name,
                };
              })
          : []
      );
      setOptionGroupList(OptionGroupResultList);
    } catch (error) {
      console.error(error);
      alert("エラーが発生しました。");
      history.replace("/auth/login");
    }
  };

  useEffect(() => {
    fetchOptionGroupList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { optionGroupList };
};

export type DisplaySubject = {
  status: Status;
  group_name: string;
  main_user_name_group: string;
  main_user_name_asmarq: string;
  first_interview_date: string;
  last_interview_date: string;
  display_expiration_date: string;
} & Subject;

export const useSubjectList = () => {
  const history = useHistory();
  const { setLoading } = useLoadingState();
  const {
    selectedGroupId,
    setScreenName,
    setPageLevel,
    isOneGroupUser,
    userId,
  } = useConsoleState();
  const [displaySubjectList, setDisplaySubjectList] = React.useState<
    DisplaySubject[]
  >([]);

  const [markedSubjectList, setMarkedSubjectList] = React.useState<
    CreateMarkedSubjectInput[]
  >([]);

  const onChangeSubjectMark = async (subject: DisplaySubject) => {
    try {
      const markedSubject = markedSubjectList.find(
        (markedSubject) => markedSubject.subject_id === subject.id
      );
      if (markedSubject?.id) {
        deleteMarkedSubjectMutation({
          id: markedSubject.id,
        });
        setMarkedSubjectList((prevMarkedSubjectList) =>
          prevMarkedSubjectList.filter(
            (markedSubject) => markedSubject.subject_id !== subject.id
          )
        );
      } else {
        const markedSubjectInput: CreateMarkedSubjectInput = {
          id: uuidv4(),
          user_id: userId,
          subject_id: subject.id,
          groups: subject.group_id,
        };

        createMarkedSubjectMutation(markedSubjectInput);
        setMarkedSubjectList((prevList) => [...prevList, markedSubjectInput]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchMarkedSubjects = async () => {
    try {
      const markedResult = await fetchMarkedSubjectByUserIdQuery(userId);
      setMarkedSubjectList(markedResult);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const result = isOneGroupUser
        ? await listSubjectsQuery(selectedGroupId)
        : await returnAvailableSubjectList(await listAllSubjectsQuery());
      if (!result) return;
      const subjectWithDisplayList = await Promise.all(
        result.map(async (subject): Promise<DisplaySubject> => {
          const status = await decideSubjectStatus(subject);
          const group_name = await getGroupQuery(subject.group_id).then(
            (group) => group?.name as string
          );
          const main_user = await returnMainUserBySubject(subject);
          const subjectDate = await returnSubjectDate(subject.id);

          return {
            ...subject,
            status,
            group_name,
            main_user_name_group: main_user.main_user_name_group,
            main_user_name_asmarq: main_user.main_user_name_asmarq,
            first_interview_date: subjectDate.first_interview_date,
            last_interview_date: subjectDate.last_interview_date,
            display_expiration_date:
              subject.expiration_date &&
              subject.expiration_date !== "2999-12-31"
                ? getDisplayMD(new Date(subject.expiration_date))
                : "未設定",
          };
        })
      );

      setDisplaySubjectList([
        ...subjectWithDisplayList
          .filter((subject) => subject.status === "DOING")
          .sort((a, b) =>
            a.last_interview_date > b.last_interview_date ? 1 : -1
          ),
        ...subjectWithDisplayList.filter(
          (subject) =>
            subject.status === "NOT_STARTED" &&
            subject.first_interview_date === "未設定"
        ),
        ...subjectWithDisplayList
          .filter(
            (subject) =>
              subject.status === "NOT_STARTED" &&
              subject.first_interview_date !== "未設定"
          )
          .sort((a, b) =>
            a.first_interview_date > b.first_interview_date ? 1 : -1
          ),
        ...subjectWithDisplayList
          .filter((subject) => subject.status === "DONE")
          .sort((a, b) =>
            a.last_interview_date < b.last_interview_date ? 1 : -1
          ),
      ]);
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
      setLoading(false);
      history.replace("/auth/login");
    }
  };

  React.useEffect(() => {
    setScreenName("案件管理");
    setPageLevel(PageLevel.subjectManagement);
    fetchSubjects();
    fetchMarkedSubjects();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    displaySubjectList,
    markedSubjectList,
    fetchSubjects,
    fetchMarkedSubjects,
    onChangeSubjectMark,
  };
};
